import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, CornerDownRight, Send, Undo2 } from "lucide-react";

/** Internal-only record discussion: comments, @mentions, replies, resolve. */
export const RecordComments = ({ etype, eid, entityTitle, entityRoute, onChanged }) => {
  const [threads, setThreads] = useState([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [users, setUsers] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get(`/collab/comments/${etype}/${eid}`)
    .then(({ data }) => setThreads(data.items || [])).catch(() => {}), [eid, etype]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [etype, eid, load]);
  useEffect(() => {
    api.get("/one-today/assignees").then(({ data }) => setUsers(data.items || [])).catch(() => {});
  }, []);

  const onChange = (e) => {
    setBody(e.target.value);
    const m = /@[\w.+-]*$/.test(e.target.value.slice(0, e.target.selectionStart));
    setMentionOpen(m);
  };

  const insertMention = (email) => {
    setBody((b) => b.replace(/@[\w.+-]*$/, `@${email} `));
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/collab/comments", {
        entity_type: etype, entity_id: eid, body: body.trim(),
        parent_id: replyTo?.id || null,
        entity_title: entityTitle, entity_route: entityRoute,
      });
      setBody(""); setReplyTo(null);
      load();
      onChanged?.();
    } catch { toast.error("Could not post comment"); }
    finally { setBusy(false); }
  };

  const resolve = async (c) => {
    const { data } = await api.post(`/collab/comments/${c.id}/resolve`);
    toast.success(data.resolved ? "Thread resolved" : "Thread reopened");
    load();
  };

  const mentionMatches = mentionOpen
    ? users.filter((u) => (u.email || "").toLowerCase()
        .includes((body.match(/@([\w.+-]*)$/)?.[1] || "").toLowerCase())).slice(0, 5)
    : [];

  return (
    <div data-testid="record-comments" className="space-y-2">
      {threads.length === 0 && (
        <div className="text-xs text-slate-400">Internal discussion — not visible to customers.</div>
      )}
      {threads.map((t) => (
        <div key={t.id} className={`border rounded-md px-2.5 py-2 ${t.resolved ? "border-emerald-100 bg-emerald-50/40" : "border-slate-100"}`}
          data-testid={`comment-thread-${t.id}`}>
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <span className="font-medium text-slate-600">{t.author_name}</span>
            <span>{(t.at || "").slice(0, 16).replace("T", " ")}</span>
            {t.resolved && <span className="text-emerald-600 inline-flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> Resolved</span>}
            <span className="ml-auto flex gap-2">
              <button onClick={() => { setReplyTo(t); inputRef.current?.focus(); }}
                data-testid={`comment-reply-${t.id}`}
                className="hover:text-slate-700">Reply</button>
              <button onClick={() => resolve(t)} data-testid={`comment-resolve-${t.id}`}
                className="hover:text-slate-700 inline-flex items-center gap-0.5">
                {t.resolved ? <><Undo2 className="h-3 w-3" /> Reopen</> : "Resolve"}
              </button>
            </span>
          </div>
          <div className={`text-[13px] mt-1 whitespace-pre-wrap break-words ${t.resolved ? "text-slate-400" : "text-slate-800"}`}>
            {t.body}
          </div>
          {(t.replies || []).map((r) => (
            <div key={r.id} className="mt-1.5 ml-3 pl-2 border-l-2 border-slate-100">
              <div className="text-[11px] text-slate-400">
                <span className="font-medium text-slate-600">{r.author_name}</span>{" "}
                {(r.at || "").slice(0, 16).replace("T", " ")}
              </div>
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{r.body}</div>
            </div>
          ))}
        </div>
      ))}

      <div className="relative">
        {replyTo && (
          <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
            <CornerDownRight className="h-3 w-3" /> Replying to {replyTo.author_name}
            <button onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-slate-700 ml-1">✕</button>
          </div>
        )}
        {mentionMatches.length > 0 && (
          <div className="absolute bottom-full mb-1 left-0 bg-white border border-slate-200 rounded-md shadow-lg w-64 z-10"
            data-testid="mention-picker">
            {mentionMatches.map((u) => (
              <button key={u.email} onClick={() => insertMention(u.email)}
                data-testid={`mention-pick-${u.email}`}
                className="w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-slate-50 flex items-center gap-2">
                <span className="text-slate-800">{u.name || u.email}</span>
                <span className="text-slate-400 ml-auto truncate max-w-[120px]">{u.email}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-1.5">
          <textarea ref={inputRef} rows={2} value={body} onChange={onChange}
            placeholder="Add an internal comment… use @email to mention"
            data-testid="comment-input"
            className="flex-1 border border-slate-200 rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none" />
          <button onClick={submit} disabled={busy || !body.trim()}
            data-testid="comment-submit"
            className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 text-white disabled:opacity-40 hover:bg-slate-800">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordComments;
